import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { useHistory } from 'react-router-dom';
import { setKanbanLaneOrder, getKanbanLaneOrder } from '../../services/companyKanbanService';
import { Button, TextField, Paper, FormControl, InputLabel, Select } from '@material-ui/core';
import { format } from 'date-fns';
import { Can } from '../../components/Can';
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import KanbanBoard from './KanbanBoard';
import CrmCreateCardModal from '../../components/CrmCreateCardModal';
import CrmCardChoiceModal from '../../components/CrmCardChoiceModal';
import CrmImportTemplateModal from '../../components/CrmImportTemplateModal';
import { downloadCrmTemplate } from '../../components/CrmImportTemplateModal/crmTemplateXlsx';
import toastError from '../../errors/toastError';

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    display: 'flex',
    padding: theme.spacing(1),
    overflowX: 'auto',
    ...theme.scrollbarStyles,
    borderRadius: '10px',
  },
  button: {
    borderRadius: '10px',
  },
  dateInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    marginRight: theme.spacing(1),
  },
  sortSelect: {
    minWidth: 150,
    marginRight: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [searchText, setSearchText] = useState("");
  const searchTextRef = useRef("");
  const startDateRef = useRef(format(new Date(), 'yyyy-MM-dd'));
  const endDateRef = useRef(format(new Date(), 'yyyy-MM-dd'));
  const queueIds = user.queues.map(queue => queue.UserQueue.queueId);

  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('sortOrder') || 'ticketNumber';
  });

  const [laneOrder, setLaneOrder] = useState(null);
  const [loadingLaneOrder, setLoadingLaneOrder] = useState(true);
  const [openCreateCard, setOpenCreateCard] = useState(false);
  const [createCardLaneId, setCreateCardLaneId] = useState(null);
  const [openChoiceModal, setOpenChoiceModal] = useState(false);
  const [openImportTemplate, setOpenImportTemplate] = useState(false);
  const isDragging = useRef(false);
  const isMoving = useRef(false);

  useEffect(() => {
    localStorage.setItem('sortOrder', sortOrder);
  }, [sortOrder]);

  // Carregar ordem das lanes do banco de dados
  useEffect(() => {
    const loadLaneOrder = async () => {
      try {
        const savedOrder = await getKanbanLaneOrder();
        setLaneOrder(savedOrder);
      } catch (error) {
        console.error('Erro ao carregar ordem das lanes:', error);
      } finally {
        setLoadingLaneOrder(false);
      }
    };

    if (user && user.id) {
      loadLaneOrder();
    }
  }, [user]);

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTags = async () => {
    try {
      const response = await api.get('/tag/kanban/');
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      fetchTickets(fetchedTags);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTickets = async (fetchedTags = tags) => {
    try {
      const { data } = await api.get('/ticket/kanban', {
        params: {
          queueIds: JSON.stringify(queueIds),
          startDate: startDateRef.current,
          endDate: endDateRef.current,
        },
      });
      setTickets(data.tickets);
      organizeLanes(fetchedTags, data.tickets);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  };

  useEffect(() => {
    const companyId = user.companyId;

    const onAppMessage = data => {
      if (['create', 'update', 'delete'].includes(data.action) && !isDragging.current && !isMoving.current) {
        fetchTickets();
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user.companyId]);

  const handleSearchClick = () => {
    fetchTickets();
  };

  const handleSearchTextChange = event => {
    setSearchText(event.target.value);
    searchTextRef.current = event.target.value;
    organizeLanes();
  };

  const handleStartDateChange = event => {
    setStartDate(event.target.value);
    startDateRef.current = event.target.value;
  };

  const handleEndDateChange = event => {
    setEndDate(event.target.value);
    endDateRef.current = event.target.value;
  };

  const updateTicket = updatedTicket => {
    setTickets(prevTickets =>
      prevTickets.map(ticket =>
        ticket.id === updatedTicket.id ? updatedTicket : ticket
      )
    );
    // Atualiza a lane diretamente sem recalcular tudo
    setLanes(prevLanes =>
      prevLanes.map(lane => ({
        ...lane,
        tickets: lane.tickets.map(t =>
          t.id === updatedTicket.id ? updatedTicket : t
        ),
      }))
    );
  };

  const getOpportunityValue = (ticket) => {
    return Number(ticket.valorVenda) || 0;
  };

  // Versão leve das lanes (sem os tickets) para o select de coluna do modal
  // de detalhes do card — evita re-renderizar todos os cards a cada troca de
  // tickets só porque a referência de `lanes` mudou.
  const laneOptions = useMemo(
    () => lanes.map(({ id, title, color }) => ({ id, title, color })),
    [lanes]
  );

  const organizeLanes = (fetchedTags = tags, fetchedTickets = tickets) => {
    const sortedTickets = [...fetchedTickets];

    if (sortOrder === 'ticketNumber') {
      sortedTickets.sort((a, b) => a.id - b.id);
    } else if (sortOrder === 'lastMessageTime') {
      sortedTickets.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    } else if (sortOrder === 'valorDesc') {
      sortedTickets.sort((a, b) => {
        const valorA = getOpportunityValue(a);
        const valorB = getOpportunityValue(b);
        return valorB - valorA;
      });
    }

    const defaultTickets = sortedTickets.filter(
      ticket => ticket.tags.length === 0
    );

    let lanesData = [
      {
        id: 'lane0',
        title: i18n.t('tagsKanban.laneDefault'),
        tickets: defaultTickets,
        color: '#757575',
      },
      ...fetchedTags.map(tag => {
        const taggedTickets = sortedTickets.filter(ticket =>
          ticket.tags.some(t => t.id === tag.id)
        );
        return {
          id: tag.id.toString(),
          title: tag.name,
          tickets: taggedTickets,
          color: tag.color || '#757575',
        };
      }),
    ];

    if (sortOrder === 'cardsDesc') {
      lanesData = [...lanesData].sort((a, b) => b.tickets.length - a.tickets.length);
    } else if (sortOrder === 'cardsAsc') {
      lanesData = [...lanesData].sort((a, b) => a.tickets.length - b.tickets.length);
    }

    // Aplicar ordem personalizada se existir (ignorar se ordenando por volume)
    if (laneOrder && laneOrder.length > 0 && sortOrder !== 'cardsDesc' && sortOrder !== 'cardsAsc') {
      const orderedLanes = [];
      const laneMap = new Map(lanesData.map(lane => [lane.id, lane]));
      
      // Adicionar lanes na ordem salva
      laneOrder.forEach(laneId => {
        if (laneMap.has(laneId)) {
          orderedLanes.push(laneMap.get(laneId));
          laneMap.delete(laneId);
        }
      });
      
      // Adicionar lanes restantes (novas lanes que não estavam na ordem salva)
      laneMap.forEach(lane => orderedLanes.push(lane));
      
      setLanes(applySearchFilter(orderedLanes));
    } else {
      setLanes(applySearchFilter(lanesData));
    }
  };

  const applySearchFilter = (lanesData) => {
    const q = searchTextRef.current.toLowerCase().trim();
    if (!q) return lanesData;
    return lanesData.map(lane => ({
      ...lane,
      tickets: lane.tickets.filter(ticket => {
        const name = ticket.contact?.name?.toLowerCase() || "";
        const lastMsg = ticket.lastMessage?.toLowerCase() || "";
        const ticketNum = String(ticket.id);
        return name.includes(q) || lastMsg.includes(q) || ticketNum.includes(q);
      })
    }));
  };

  useEffect(() => {
    if (!loadingLaneOrder && !isMoving.current) {
      organizeLanes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, tickets, sortOrder, laneOrder, loadingLaneOrder]);

  const handleCardMove = async (ticketId, targetLaneId, sourceLaneId, sourceIndex, destIndex) => {
    const ticketIdInt = parseInt(ticketId, 10);

    // Bloqueia organizeLanes e socket durante o movimento
    isMoving.current = true;

    // A tag da coluna destino, para o card carregar a `tags` certa mesmo
    // antes do fetch de confirmação — sem isso, o objeto ticket mantinha as
    // tags antigas após o drag-and-drop, e telas que derivam a coluna a
    // partir de `ticket.tags` (ex.: modal de detalhes do card) mostravam a
    // coluna de origem em vez da coluna pra onde o card foi arrastado.
    const destTag = tags.find(t => t.id.toString() === targetLaneId);
    const newTags = targetLaneId !== 'lane0' && destTag ? [destTag] : [];

    // Update otimista: move o card nas lanes imediatamente
    setLanes(prevLanes => {
      const newLanes = prevLanes.map(lane => ({ ...lane, tickets: [...lane.tickets] }));
      const sourceLane = newLanes.find(l => l.id === sourceLaneId);
      const destLane = newLanes.find(l => l.id === targetLaneId);
      if (!sourceLane || !destLane) return prevLanes;

      const [movedCard] = sourceLane.tickets.splice(sourceIndex, 1);
      destLane.tickets.splice(destIndex, 0, { ...movedCard, tags: newTags });
      return newLanes;
    });

    // Mantém `tickets` (a fonte de verdade usada por organizeLanes e
    // repassada aos cards) em sincronia com a mesma mudança de tags.
    setTickets(prevTickets =>
      prevTickets.map(t => (t.id === ticketIdInt ? { ...t, tags: newTags } : t))
    );

    try {
      await api.delete(`/ticket-tags/${ticketIdInt}`);
      if (targetLaneId !== 'lane0') {
        await api.put(`/ticket-tags/${ticketIdInt}/${targetLaneId}`);
      }
    } catch (err) {
      console.log(err);
      fetchTickets();
    } finally {
      isMoving.current = false;
    }
  };

  const handleAddColumnClick = () => {
    history.push('/tagsKanban');
  };

  const handleCardCreated = () => {
    fetchTickets();
  };

  // Chamado pelo "+" de uma coluna específica: vai direto para a criação
  // de um card único, já com a coluna pré-selecionada.
  const handleOpenCreateCard = (laneId = null) => {
    setCreateCardLaneId(laneId);
    setOpenCreateCard(true);
  };

  // Chamado pelo botão "Criar Card" do header: abre o modal de escolha
  // entre criar um card único, importar em massa ou exportar o template.
  const handleOpenCardMenu = () => {
    setOpenChoiceModal(true);
  };

  const handleExportTemplate = async () => {
    try {
      const [{ data }, { data: allQueues }] = await Promise.all([
        api.get('/tickets/crm-template-data'),
        api.get('/queue'),
      ]);
      const rows = data.rows || [];
      if (rows.length === 0) {
        toast.warn('Nenhum card ativo no CRM para exportar ainda.');
        return;
      }
      await downloadCrmTemplate(rows, {
        laneNames: tags.map(t => t.name),
        queueNames: (allQueues || []).map(q => q.name),
      });
      toast.success('Template exportado com sucesso!');
    } catch (err) {
      toastError(err);
    }
  };

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
  };

  const handleLaneReorder = async (sourceIndex, destinationIndex) => {
    // Verificar se o usuário é admin
    if (user.profile !== 'admin') {
      toast.error('Apenas administradores podem reordenar as colunas do CRM');
      return;
    }

    const newLanes = Array.from(lanes);
    const [reorderedLane] = newLanes.splice(sourceIndex, 1);
    newLanes.splice(destinationIndex, 0, reorderedLane);
    
    setLanes(newLanes);
    
    // Salvar nova ordem no banco de dados
    const newLaneOrder = newLanes.map(lane => lane.id);
    setLaneOrder(newLaneOrder);
    
    try {
      await setKanbanLaneOrder(newLaneOrder);
      toast.success('Ordem das colunas atualizada com sucesso');
    } catch (error) {
      console.error('Erro ao salvar ordem das colunas:', error);
      toast.error('Erro ao salvar ordem das colunas');
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>CRM</Title>
        <MainHeaderButtonsWrapper>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.sortSelect}
          >
            <InputLabel htmlFor="sort-order-select">Ordenar por</InputLabel>
            <Select
              native
              value={sortOrder}
              onChange={handleSortOrderChange}
              label="Ordenar por"
              inputProps={{
                name: 'sortOrder',
                id: 'sort-order-select',
              }}
            >
              <option value="ticketNumber">Número do Ticket</option>
              <option value="lastMessageTime">Última Mensagem</option>
              <option value="valorDesc">Valor (maior para menor)</option>
              <option value="cardsDesc">Volume de cards (maior para menor)</option>
              <option value="cardsAsc">Volume de cards (menor para maior)</option>
            </Select>
          </FormControl>
          <TextField
            label="Buscar nome ou conteúdo"
            type="text"
            value={searchText}
            onChange={handleSearchTextChange}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            className={classes.dateInput}
            size="small"
            style={{ minWidth: 220 }}
          />
          <TextField
            label="Data de início"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            className={classes.dateInput}
            size="small"
          />
          <TextField
            label="Data de fim"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            className={classes.dateInput}
            size="small"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearchClick}
            className={classes.button}
          >
            Buscar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenCardMenu}
            className={classes.button}
          >
            Criar Card
          </Button>
          <Can
            role={user.profile}
            perform="dashboard:view"
            yes={() => (
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddColumnClick}
                className={classes.button}
              >
                Adicionar/Editar colunas
              </Button>
            )}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper variant="outlined" className={classes.mainPaper}>
        <KanbanBoard
          lanes={lanes}
          laneOptions={laneOptions}
          onCardMove={handleCardMove}
          onLaneReorder={handleLaneReorder}
          updateTicket={updateTicket}
          isAdmin={user.profile === 'admin'}
          onDragStart={() => { isDragging.current = true; }}
          onDragEnd={() => { isDragging.current = false; }}
          onCreateCard={handleOpenCreateCard}
        />
      </Paper>

      <CrmCreateCardModal
        open={openCreateCard}
        onClose={() => { setOpenCreateCard(false); setCreateCardLaneId(null); }}
        tags={tags}
        initialLaneId={createCardLaneId}
        onCreated={handleCardCreated}
      />

      <CrmCardChoiceModal
        open={openChoiceModal}
        onClose={() => setOpenChoiceModal(false)}
        onChooseSingle={() => handleOpenCreateCard()}
        onChooseImport={() => setOpenImportTemplate(true)}
        onChooseExport={handleExportTemplate}
      />

      <CrmImportTemplateModal
        open={openImportTemplate}
        onClose={() => setOpenImportTemplate(false)}
        tags={tags}
        onImported={handleCardCreated}
      />
    </MainContainer>
  );
};

export default Kanban;
